import t from '../../../labels/labelling-utils';
import {
  DRIVERS_RANKING_FREE_PRACTICE_RESULTS,
  DRIVERS_RANKING_POINTS,
  DRIVERS_RANKING_QUALIFYING_RESULTS,
  DRIVERS_RANKING_RACE_RESULTS,
  DRIVERS_RANKING_RACE_STARTING_GRID,
  DriversRankingVariants,
} from '../../../typings/drivers-ranking';
import { RACE_STATUS_PLAYED, RACE_STATUS_PLAYING, RaceSessionType } from '../../../typings/race-session';
import { isNotEmptyArray } from '../../../utils/array';
import { isMobile } from '../../../utils/device-utils';
import { isPlaying, isQualifyingPhase, isRankingPoints } from '../../../utils/race-status';
import { IAccordionTitle } from '../../organisms/accordion/accordion';
import { ITopDriversRow, ITopDriversTable } from '../../organisms/top-drivers-ranking-table/top-drivers-ranking-table';

const FALLBACK_ACCORDION_NAME = 'default_accordion_name';

/**
 * Variant title map
 */
const getStandingsVariantsTitleMap = () => {
  return {
    [DRIVERS_RANKING_POINTS]: t('drivers.standing.total.points.results'),
    [DRIVERS_RANKING_FREE_PRACTICE_RESULTS]: t('drivers.standing.free.practice.results'),
    [DRIVERS_RANKING_QUALIFYING_RESULTS]: t('drivers.standing.qualifying.results'),
    [DRIVERS_RANKING_RACE_STARTING_GRID]: t('drivers.standing.race.starting.grid.results'),
    [DRIVERS_RANKING_RACE_RESULTS]: t('drivers.standing.race.results'),
  };
};

/**
 * Function to validate stading list
 * @param rows rows of a standing list
 */
export const isValidStandingsList = (rows: ITopDriversTable[]) => Boolean(rows && rows.length);

/**
 * StandingList has at least one PLAYED session
 * @param standingsList standilinList
 */
export const hasPlayedSession = (standingsList: ITopDriversTable[]): boolean =>
  standingsList.filter((topDriversTable: ITopDriversTable) => topDriversTable.status === RACE_STATUS_PLAYED).length > 0;

/**
 * Utility function for create Accordion name
 * @param accordionTitle accordion title Object
 */
export const getAccordionName = (accordionTitle: IAccordionTitle): string =>
  accordionTitle.text?.replace(/[,\s]+/g, '_') || FALLBACK_ACCORDION_NAME;

/**
 * Utility function to get StandingList title
 * @param rankingVariant variant
 * @param competitionName competition Name
 */
export const getStandingsTitle = (
  rankingVariant: DriversRankingVariants,
  competitionName: string | undefined,
  showTitle: boolean = false
) => {
  const standingsVariantsTitleMap = getStandingsVariantsTitleMap();
  const title = showTitle ? standingsVariantsTitleMap[rankingVariant] : '';
  return isRankingPoints(rankingVariant) ? `${standingsVariantsTitleMap[rankingVariant]} ${competitionName}` : title;
};

export const getTopDriversTableProps = (index: number = 0) => ({
  key: `top-drivers-${index}`,
  className: 'top-drivers',
  showRowSelected: isMobile(),
  showTitles: true,
});

/**
 * Show LEGEND only if ROWS have AT LEAST one "isFastestLap=true"
 * @param standingsList array of SESSIONS
 */
export const hasFastestLaps = (standingsList: ITopDriversTable[]): boolean => {
  const fastestLaps: ITopDriversRow[] =
    standingsList
      .map((topDriversTable: ITopDriversTable) =>
        topDriversTable.rows.filter((topDriversRow: ITopDriversRow) => Boolean(topDriversRow.isFastestLap))
      )
      .flat()
      .filter((topDriversRow: ITopDriversRow) => Boolean(topDriversRow.isFastestLap)) ?? [];

  return isNotEmptyArray(fastestLaps);
};

/** Has only one Q session played */
const hasOnlyOneQualifyingSessionPlayedPlaying = (
  rankingVariant: DriversRankingVariants,
  standingsList: Array<ITopDriversTable>
) =>
  standingsList.length > 1 &&
  isQualifyingPhase(rankingVariant) &&
  standingsList.filter((elm) => elm.status === RACE_STATUS_PLAYED || elm.status === RACE_STATUS_PLAYING).length === 1;

/** Not has played Q sessions*/
const notHasQualifyingSessionsPlayed = (rankingVariant: DriversRankingVariants, standingsList: ITopDriversTable[]) =>
  standingsList.length > 1 &&
  isQualifyingPhase(rankingVariant) &&
  standingsList.filter((elm) => elm.status === RACE_STATUS_PLAYED).length === 0;

/*
 * return isSingleRankingTable depending on:
 * @param rankingVariant variant
 * @param standingsList standing list
 * @returns bolean on isSingleRankingTable
 */
export const isSingleRankingTable = (rankingVariant: DriversRankingVariants, standingsList: ITopDriversTable[]) =>
  !isQualifyingPhase(rankingVariant) ||
  (isQualifyingPhase(rankingVariant) && standingsList?.length === 1) ||
  hasOnlyOneQualifyingSessionPlayedPlaying(rankingVariant, standingsList) ||
  notHasQualifyingSessionsPlayed(rankingVariant, standingsList);

/**
 * Get the only one played Qualifying or Normal session in array of sessions
 * @param rankingVariant varinat of the table
 * @param standingsList list of sessions
 * @returns session table to show
 */
export const getSingleTable = (rankingVariant: DriversRankingVariants, standingsList: ITopDriversTable[]) => {
  if (isSingleRankingTable(rankingVariant, standingsList) && isQualifyingPhase(rankingVariant)) {
    if (standingsList.length > 1) {
      const playedSession = standingsList.filter((table) => table.status === RACE_STATUS_PLAYED)[0];
      return playedSession || standingsList[0];
    }
  }
  return standingsList[0];
};

/**
 * Return the rows for not played status
 * @param standingsList standing list
 * @returns Top drivers Table rows
 */
export const getNotPlayedStandingListRows = (standingsList: ITopDriversTable[]) =>
  isPlaying(standingsList[0].status) ? standingsList[0].rows : [];

export const getSEOTitle = (sessionName: RaceSessionType): string => {
  switch (sessionName) {
    case 'FP1':
      return 'Prove Libere 1';
    case 'FP2':
      return 'Prove Libere 2';
    case 'FP3':
      return 'Prove Libere 3';
    case 'FP4':
      return 'Prove Libere 4';
    case 'Q':
      return 'Qualifiche';
    case 'SS':
      return 'Sprint Shootout';
    case 'SPRINT':
      return 'Sprint Race';
    default:
      return 'Gran Premio';
  }
};
